<script setup lang="ts">
import ConfirmationModal from '@/components/other/ConfirmationModal.vue'
import { usePejer } from '@/Pejer'
import { InvoicesEmbed } from '@/Pejer/tables/Invoices'
import { PaymentsEmbed } from '@/Pejer/tables/Payments'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { CheckCircle, Loader2 } from 'lucide-vue-next'
import { Button } from 'pejer/src/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'pejer/src/components/ui/table'
import Textarea from 'pejer/src/components/ui/textarea/Textarea.vue'
import { computed, onMounted, ref } from 'vue'
import PaymentRefundRow from './PaymentRefundRow.vue'

const props = defineProps<{
  paymentReferenceID: string
}>()

const emits = defineEmits<{
  (e: 'updatePayment', id: number, newAmount: number): void
  (e: 'close'): void
}>()

const feeAmount = ref<number | null>(null)
const newMoneySplit = ref<
  (PaymentsEmbed.MoneySplit & {
    newAmount?: number
  })[]
>([])

const pejer = usePejer()

const {
  isLoading,
  data,
  refetch,
  isError: isQueryError,
  error: queryError,
} = useQuery({
  queryKey: ['payments', props.paymentReferenceID],
  queryFn: async () => {
    const res = await pejer.getOne('payments', props.paymentReferenceID)
    if ('error' in res) throw new Error(res.error)

    feeAmount.value = res.data.moneySplit.reduce((acc, split) => {
      if (split.isFee) {
        return acc + split.amount
      }
      return acc
    }, 0)

    newMoneySplit.value = res.data
      .moneySplit!.map((split) => {
        return {
          ...split,
          newAmount: split.amount,
        }
      })
      .sort((a, b) => {
        if (a.isFee) return 1
        if (b.isFee) return -1

        return a.splitID! - b.splitID!
      })

    // moneySplit.value.forEach((split) => {
    //   if (!(split.externalID! in moneySplitMap.value)) {
    //     moneySplitMap.value[split.externalID!] = []
    //   }
    //   moneySplitMap.value[split.externalID!].push(split)
    // })

    return res
  },
  enabled: false,
})

const queryClient = useQueryClient()
const { status, isPending, isError, error, mutate } = useMutation({
  mutationFn: async (
    args: InvoicesEmbed.AdjustOrRefundPaymentRequest & { fullRefund?: boolean }
  ) => {
    var fullRefund = args.fullRefund ?? false
    delete args.fullRefund

    const values = {
      moneySplit: [...args.moneySplit].map((split) => ({
        splitID: split.splitID,
        amount: split.amount * 100,
      })),
      refundDescription: args.refundDescription,
    } as InvoicesEmbed.AdjustOrRefundPaymentRequest

    if (!fullRefund) {
      const res = await pejer.fetch(pejer.urls.payments['refund-partially'], {
        pathArgs: {
          id: props.paymentReferenceID,
        },
        body: values,
      })
      if ('error' in res) throw res.error

      pejer.postToParent('payment.refunded.partially', {
        paymentReferenceId: props.paymentReferenceID,
      })
      return res
    }

    const res = await pejer.fetch(pejer.urls.payments.refund, {
      pathArgs: {
        id: props.paymentReferenceID,
      },
      body: values,
    })
    if ('error' in res) throw res.error

    pejer.postToParent('payment.refunded', {
      paymentReferenceId: props.paymentReferenceID,
    })
    if ('error' in res) throw res.error
    return res
  },

  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ['payments'],
    })
    queryClient.invalidateQueries({
      queryKey: ['invoices'],
    })
  },
})

var refundDescription = ref<string>('')
function updatePayment() {
  const moneySplitArray: InvoicesEmbed.AdjustOrRefundPaymentRequest['moneySplit'] =
    newMoneySplit.value.map((split) => {
      return {
        splitID: split.splitID!,
        amount: split.newAmount!,
      }
    })

  mutate({
    paymentReferenceID: props.paymentReferenceID,
    moneySplit: moneySplitArray,
    refundDescription: refundDescription.value,
    fullRefund: false,
  })
}

function refundPayment() {
  const moneySplitArray: InvoicesEmbed.AdjustOrRefundPaymentRequest['moneySplit'] =
    newMoneySplit.value.map((split) => {
      return {
        splitID: split.splitID!,
        amount: 0,
      }
    })

  mutate({
    paymentReferenceID: props.paymentReferenceID,
    moneySplit: moneySplitArray,
    refundDescription: refundDescription.value,
    fullRefund: true,
  })
}

const newGrossAmountSum = computed(() => {
  if (!data.value) return undefined
  let amount = newMoneySplit.value.reduce(
    (acc, split) => acc + (split.isFee ? split.amount : split.newAmount!),
    0
  )
  // var moneySplitList = newMoneySplit.value.reduce((acc, split) => acc + (split.isFee ? split.amount : split.newAmount!), 0)
  // var totalNonFeeAmount = moneySplitList.reduce((acc, splitGroup) => {
  //   return (
  //     acc + (splitGroup.reduce((acc, split) => acc + (split.isFee ? 0 : split.newAmount!), 0) || 0)
  //   )
  // }, 0)

  // moneySplitList.forEach((splitGroup) => {
  //   const main = splitGroup.find((split) => !split.isFee)
  //   if (main && main.newAmount  && main.newAmount !== 0) {
  //     amount += main.newAmount
  //     amount += splitGroup
  //       .filter((split) => split.isFee)
  //       .reduce((acc, split) => acc + split.amount!, 0)
  //   } else if (totalNonFeeAmount !== 0) {
  //     amount += splitGroup
  //       .filter((split) => split.isFee)
  //       .reduce((acc, split) => acc + split.amount!, 0)
  //   }
  // })

  return amount
})

function formatNumber(value?: number | null) {
  if (value === null || value == undefined) return '-'

  return value.toLocaleString('nb-no', {
    currency: 'NOK',
    style: 'currency',
    currencyDisplay: 'code',
  })
}

function updateMoneySplitItem(id: number, newAmount: number) {
  const index = newMoneySplit.value.findIndex((item) => item.splitID === id)

  if (index === -1) return

  newMoneySplit.value[index].newAmount = newAmount
  // moneySplitMap.value[newMoneySplit.value[index].externalID!].find(
  //   (split) => split.splitID === id
  // )!.newAmount = newAmount
}

onMounted(() => {
  refetch()
})
</script>
<template>
  <div class="pb-4 h-full">
    <div v-if="status === 'success'" class="text-gray-800 w-fit rounded-md text-sm p-4 mt-4">
      <span class="font-md text-green-500 gap-2 flex items-center">
        <CheckCircle :size="20" /> Betaling oppdatert</span
      >
    </div>
    <template v-else>
      <div
        :class="{
          'opacity-50': isPending,
        }"
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead class="text-sm font-semibold m-0 w-[10px]" />
              <TableHead class="text-sm font-semibold m-0"> Beløp </TableHead>
              <TableHead class="text-sm font-semibold m-0"> Nytt Beløp </TableHead>
              <TableHead class="text-sm font-semibold m-0"> Beskrivelse </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow v-if="isLoading">
              <TableCell class="flex justify-center items-center w-full">
                <Loader2 :size="20" class="animate-spin mr-2" />
                Laster...
              </TableCell>
            </TableRow>
            <TableRow v-else-if="isQueryError">
              <TableCell colspan="4" class="text-red-500 text-sm font-semibold p-4">
                Kunne ikke laste inn betaling. Prøv igjen senere. <br />
                Årsak: {{ queryError }}
              </TableCell>
            </TableRow>
            <template v-else-if="newMoneySplit">
              <PaymentRefundRow
                v-for="split in newMoneySplit"
                :key="split.splitID"
                :money-split="split"
                @update-payment="updateMoneySplitItem"
              />
            </template>

            <TableRow>
              <TableCell />
              <TableCell class="w-fit" colspan="2">
                <h5>Sum</h5>
                <span
                  class="text-muted-foreground my-2"
                  :class="{
                    'text-xs': newGrossAmountSum !== data?.data.grossAmount,
                  }"
                >
                  Opprinnelig beløp:
                  <span
                    :class="{
                      'line-through': newGrossAmountSum !== data?.data.grossAmount,
                    }"
                    >{{ formatNumber(data?.data.grossAmount) }}</span
                  >
                </span>
                <p
                  v-if="newGrossAmountSum !== data?.data.grossAmount"
                  class="text-muted-foreground my-1"
                >
                  Nytt beløp:
                  {{ formatNumber(newGrossAmountSum) }}
                </p>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
        <div class="flex flex-col my-6">
          <h3 class="text-sm text-slate-500 font-semibold m-0">
            Refusjonsbeskrivelse <span class="text-red-500">*</span>
          </h3>
          <Textarea
            v-model="refundDescription"
            placeholder="T-skjorten refunderes fullt"
          ></Textarea>
        </div>
        <div class="flex justify-between items-center">
          <div class="flex flex-col-reverse sm:flex-row gap-2 items-center">
            <Button variant="outline" @click="$emit('close')">Avbryt</Button>
            <ConfirmationModal word="refunder" @confirmed="refundPayment">
              <template #description>
                <span>
                  Bekreft refundering av fullt beløp
                  <span class="font-bold">{{ formatNumber(data?.data.grossAmount) }}</span>
                </span>
              </template>
              <Button
                class="h-full"
                :class="{
                  'border-red-500 text-red-500': newGrossAmountSum !== 0,
                }"
                :disabled="isPending || refundDescription.length < 1"
                :variant="newGrossAmountSum !== 0 ? 'outline' : 'default'"
              >
                <span>
                  Refunder hele beløpet
                  <!-- ({{ formatNumber(data?.data.kidNorwayInvoice.paidAmount! ) }}) -->
                </span>
              </Button>
            </ConfirmationModal>
            <div class="flex flex-col min-h-full items-center justify-center">
              <ConfirmationModal word="refunder" @confirmed="updatePayment">
                <template #description>
                  <span
                    >Bekreft endring av fakturabeløp fra
                    <span class="font-bold">{{ formatNumber(data?.data.grossAmount) }}</span>
                    til
                    <span class="font-bold">{{ formatNumber(newGrossAmountSum) }}</span>
                  </span>
                </template>
                <Button
                  v-if="newGrossAmountSum! !== 0"
                  class="min-h-fit h-full"
                  :disabled="
                    isPending ||
                    newGrossAmountSum! === data?.data.grossAmount ||
                    refundDescription.length < 1
                  "
                >
                  <span v-if="newGrossAmountSum == data?.data.grossAmount"> Ingen endring </span>
                  <span v-else
                    >Endre fakturabeløpet til
                    <span class="font-bold">{{ formatNumber(newGrossAmountSum) }}</span></span
                  >
                </Button>
              </ConfirmationModal>
              <span
                v-if="
                  data?.data.grossAmount !== undefined &&
                  newGrossAmountSum !== undefined &&
                  data?.data.grossAmount > newGrossAmountSum
                "
                class="text-muted-foreground text-sm"
                >Kunden refunderes
                {{ formatNumber(data?.data.grossAmount - newGrossAmountSum) }}</span
              >
            </div>
          </div>
          <!-- <div class="flex items-center gap-2">
          <ConfirmationModal word="godkjenn" @confirmed="approvePayment">
            <template #description>
              <span>
                Bekreft godkjenning av faktura, prisen på fakturaen vil bli endret til
                <span class="font-bold">{{
                  formatNumber(data?.data.kidNorwayInvoice.paidAmount)
                }}</span>
              </span>
            </template>
            <Button class="h-full" :disabled="isPending || status === 'success'" variant="outline">
              <span> Godkjenn faktura som betalt </span>
            </Button>
          </ConfirmationModal>
        </div> -->
        </div>
      </div>
      <span v-if="isPending" class="text-muted-foreground text-sm flex mt-4">
        <Loader2 :size="20" class="animate-spin mr-2" />
        Laster...
      </span>
      <span v-else-if="isError" class="text-red-500 text-sm font-semibold mt-4">
        Kunne ikke oppdatere. <br />
        Årsak: {{ error }}
      </span>
    </template>
  </div>
</template>
